import { Flame, Gamepad2, Grid2X2, UsersRound } from "lucide-react";

import { cn } from "@/engine/support/classNames";

export type RoomMode = "fire" | "bubbles" | "plain" | "teams";

export interface RoomModeOption {
  id: RoomMode;
  name: string;
  description: string;
}

/** The same icons the brandbook's left navigation uses for the four plugins. */
const MODE_ICONS: Record<RoomMode, typeof Flame> = {
  fire: Flame,
  bubbles: UsersRound,
  plain: Grid2X2,
  teams: Gamepad2,
};

export interface RoomModePickerProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onChange"
> {
  value: RoomMode;
  options: readonly RoomModeOption[];
  onChange?: ((mode: RoomMode) => void) | undefined;
  /** The application's handle on each mode's button, for its browser tests. */
  testIds?: Partial<Record<RoomMode, string | undefined>> | undefined;
}

/** Selects one of the four room arrangements without replacing the people in it. */
export function RoomModePicker({
  value,
  options,
  onChange,
  testIds = {},
  className,
  ...props
}: RoomModePickerProps) {
  return (
    <div className={cn("grid grid-cols-2 gap-2.5", className)} {...props}>
      {options.map((option) => {
        const Icon = MODE_ICONS[option.id];
        return (
          <button
            key={option.id}
            type="button"
            title={option.description}
            aria-pressed={value === option.id}
            data-testid={testIds[option.id]}
            onClick={() => onChange?.(option.id)}
            className={cn(
              "flex flex-col gap-1 rounded-xl border border-raise px-3 py-2.5 text-left transition-colors duration-(--motion-base)",
              "hover:border-primary/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              value === option.id && "border-primary/40 bg-primary/15",
            )}
          >
            <span className="flex items-center gap-2.5">
              <Icon
                className={cn(
                  "size-4 shrink-0",
                  value === option.id
                    ? "text-primary"
                    : "text-muted-foreground",
                )}
                aria-hidden="true"
              />
              <span className="truncate text-sm font-medium">
                {option.name}
              </span>
            </span>
            <span className="text-xs leading-4.5 text-muted-foreground">
              {option.description}
            </span>
          </button>
        );
      })}
    </div>
  );
}
